import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Snowflake, Power, Minus, Plus, RefreshCw, Pencil, Save, X, Thermometer, Wifi, WifiOff, Sun, Flame, CloudSun, AlertTriangle } from "lucide-react";
import axios from "axios";
import { toast } from "../../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const c = axios.create({ baseURL: `${BACKEND_URL}/api` });
c.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("admin_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const MODE_LABEL = { 0: "Ventola", 1: "Riscaldamento", 2: "Freddo", 3: "Deumidificatore", 4: "Auto", 5: "Eco" };

// Hi-Sense ConnectLife admin panel. One card per air-conditioner grouped
// by room; only exposes power + target temperature (mode/fan speed stay as
// the owner set them in the Hi-Sense app on their phone).
export default function HisenseACPanel() {
  const [appliances, setAppliances] = useState([]);
  const [roomErrors, setRoomErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [editingPuid, setEditingPuid] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await c.get("/hisense/appliances");
      setAppliances(data.appliances || []);
      setRoomErrors(data.room_errors || {});
    } catch (e) {
      toast({ title: "Errore Hi-Sense", description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    // Poll less aggressively than lights — ACs don't flip states rapidly.
    const t = setInterval(() => { if (!editingPuid) load(); }, 15000);
    return () => clearInterval(t);
  }, [load, editingPuid]);

  const call = async (puid, path, payload) => {
    setBusy((b) => ({ ...b, [puid]: true }));
    try {
      await c.post(`/hisense/appliances/${puid}/${path}`, payload || {});
      setTimeout(load, 900);
    } catch (e) {
      toast({ title: "Comando fallito", description: e?.response?.data?.detail || e.message });
    } finally { setBusy((b) => ({ ...b, [puid]: false })); }
  };

  if (loading) return (
    <div className="rounded-2xl bg-white border border-[#e8dcc8] p-8 flex justify-center">
      <Loader2 className="h-5 w-5 animate-spin text-[#4a1c17]" />
    </div>
  );

  // Group by room for nicer layout.
  const byRoom = {};
  appliances.forEach((a) => {
    (byRoom[a.room] = byRoom[a.room] || []).push(a);
  });
  const rooms = Object.keys(byRoom).sort();

  return (
    <div className="rounded-2xl bg-white border border-[#e8dcc8] p-4 space-y-3" data-testid="hisense-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-serif text-lg font-bold text-[#2b1410] flex items-center gap-2">
          <Snowflake className="h-5 w-5 text-sky-500" /> Climatizzatori Hi-Sense
        </h3>
        <button onClick={load} className="text-xs text-[#7c5a4e] hover:text-[#2b1410] inline-flex items-center gap-1">
          <RefreshCw className="h-3 w-3" /> Aggiorna
        </button>
      </div>

      <SeasonalSwitcher onDone={load} />

      {rooms.length === 0 ? (
        <div className="text-center text-sm text-[#7c5a4e] py-6">Nessun account Hi-Sense configurato.</div>
      ) : rooms.map((room) => (
        <div key={room} className="rounded-xl border border-[#e8dcc8] p-3">
          <div className="text-xs uppercase tracking-wider font-serif font-bold text-[#4a1c17] mb-2">{room}</div>
          {roomErrors[room] && (
            <div className="mb-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-xs px-3 py-2 flex items-start gap-2" data-testid={`hisense-room-error-${room}`}>
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /><span>{roomErrors[room]}</span>
            </div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {byRoom[room].map((a) => (
              <ACCard
                key={a.puid}
                ac={a}
                busy={!!busy[a.puid]}
                editing={editingPuid === a.puid}
                onEdit={() => setEditingPuid(a.puid)}
                onEditClose={() => { setEditingPuid(null); load(); }}
                onToggle={() => call(a.puid, a.power === 1 ? "off" : "on")}
                onTemp={(temp) => call(a.puid, "temp", { temp })}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ACCard({ ac, busy, editing, onEdit, onEditClose, onToggle, onTemp }) {
  const on = ac.power === 1;
  const target = ac.target_temp ?? 22;
  const [pending, setPending] = useState(target);
  useEffect(() => { setPending(target); }, [target]);
  const changeTemp = (delta) => {
    const next = Math.max(16, Math.min(30, pending + delta));
    setPending(next);
    onTemp(next);
  };
  return (
    <div className={`rounded-lg border p-3 ${on ? "border-sky-400 bg-sky-50" : "border-[#e8dcc8] bg-white"}`} data-testid={`ac-${ac.puid}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {editing
            ? <NameEditor ac={ac} onDone={onEditClose} />
            : (
              <div className="flex items-center gap-1 min-w-0">
                <span className="font-semibold text-sm text-[#2b1410] truncate">{ac.name}</span>
                <button onClick={onEdit} className="text-[#7c5a4e] hover:text-[#2b1410] p-0.5 shrink-0">
                  <Pencil className="h-3 w-3" />
                </button>
              </div>
            )}
          <div className="text-[10px] text-[#7c5a4e] flex items-center gap-1 mt-0.5">
            {ac.online ? <Wifi className="h-2.5 w-2.5 text-emerald-600" /> : <WifiOff className="h-2.5 w-2.5 text-neutral-400" />}
            <span>{ac.online ? "online" : "offline"}</span>
            {ac.work_mode != null && <span>· {MODE_LABEL[ac.work_mode] || "—"}</span>}
          </div>
        </div>
        <button
          onClick={onToggle}
          disabled={busy || !ac.online}
          data-testid={`ac-toggle-${ac.puid}`}
          className={`shrink-0 h-9 w-9 rounded-full flex items-center justify-center ${on ? "bg-sky-500 text-white" : "bg-neutral-300 text-neutral-700"} disabled:opacity-40`}
          title={on ? "Spegni" : "Accendi"}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4" />}
        </button>
      </div>

      <div className="mt-2 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase text-[#7c5a4e]">Ambiente</div>
          <div className="text-lg font-bold text-[#2b1410] flex items-center gap-0.5">
            <Thermometer className="h-4 w-4 text-orange-500" /> {ac.ambient_temp ?? "—"}°
          </div>
        </div>
        <div className="text-center">
          <div className="text-[10px] uppercase text-[#7c5a4e]">Target</div>
          <div className="flex items-center gap-1">
            <button onClick={() => changeTemp(-1)} disabled={!on || busy}
              data-testid={`ac-temp-down-${ac.puid}`}
              className="h-7 w-7 rounded-full bg-white border border-[#e8dcc8] hover:bg-[#faf3ee] flex items-center justify-center disabled:opacity-30">
              <Minus className="h-3.5 w-3.5" />
            </button>
            <div className="w-10 text-center font-bold text-lg text-[#4a1c17]" data-testid={`ac-temp-${ac.puid}`}>
              {pending}°
            </div>
            <button onClick={() => changeTemp(+1)} disabled={!on || busy}
              data-testid={`ac-temp-up-${ac.puid}`}
              className="h-7 w-7 rounded-full bg-white border border-[#e8dcc8] hover:bg-[#faf3ee] flex items-center justify-center disabled:opacity-30">
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function NameEditor({ ac, onDone }) {  const [name, setName] = useState(ac.name);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await c.post("/hisense/appliances/rename", { puid: ac.puid, name });
      onDone();
    } catch (e) { toast({ title: "Errore", description: e?.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex gap-1 min-w-0">
      <input value={name} onChange={(e) => setName(e.target.value)}
        className="flex-1 rounded border border-[#e8dcc8] bg-white px-2 py-1 text-xs min-w-0"
        placeholder="Nome AC" />
      <button onClick={save} disabled={busy} className="rounded bg-[#4a1c17] text-white px-2 py-1 text-xs">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
      </button>
      <button onClick={onDone} className="rounded border border-[#e8dcc8] px-2 py-1 text-xs"><X className="h-3 w-3" /></button>
    </div>
  );
}

// Big seasonal switch — one click applies Cool/Heat to every AC. Paired
// with a live Open-Meteo widget showing outdoor temp + 7-day average so
// the owner sees what the weather actually is, not what the calendar says.
function SeasonalSwitcher({ onDone }) {
  const [meteo, setMeteo] = useState(null);
  const [busy, setBusy] = useState(null); // "cool" | "heat" | null

  useEffect(() => {
    let cancel = false;
    c.get("/hisense/meteo").then(({ data }) => { if (!cancel) setMeteo(data); }).catch(() => {});
    const t = setInterval(() => {
      c.get("/hisense/meteo").then(({ data }) => { if (!cancel) setMeteo(data); }).catch(() => {});
    }, 3600_000); // refresh hourly
    return () => { cancel = true; clearInterval(t); };
  }, []);

  const apply = async (mode) => {
    const label = mode === "cool" ? "Freddo (Estate)" : "Riscaldamento (Inverno)";
    if (!confirm(`Applicare "${label}" a TUTTI i climatizzatori delle 7 camere?`)) return;
    setBusy(mode);
    try {
      const { data } = await c.post("/hisense/seasonal", { mode });
      toast({
        title: `${label} · ${data.applied}/${data.total} AC aggiornati`,
        description: data.errors?.length ? `${data.errors.length} errori` : "Tutti aggiornati",
      });
      onDone?.();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e.message });
    } finally { setBusy(null); }
  };

  const suggested = meteo?.suggestion;
  return (
    <div className="rounded-xl bg-gradient-to-br from-sky-50 to-amber-50 border border-[#e8dcc8] p-3 space-y-2" data-testid="ac-seasonal">
      {meteo && (
        <div className="flex items-center gap-2 text-xs text-[#4a1c17] flex-wrap">
          <CloudSun className="h-4 w-4 text-amber-600" />
          <span><b>Fuori a {meteo.location}</b>: {meteo.current_temp}°C · media max 7g: {meteo.avg_max_7d}°C</span>
          {suggested === "cool" && <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-sky-500 text-white text-[10px] px-2 py-0.5"><Snowflake className="h-2.5 w-2.5" /> Consiglio: Freddo</span>}
          {suggested === "heat" && <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-orange-600 text-white text-[10px] px-2 py-0.5"><Flame className="h-2.5 w-2.5" /> Consiglio: Riscaldamento</span>}
          {suggested === "auto" && <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-neutral-600 text-white text-[10px] px-2 py-0.5">Consiglio: Auto</span>}
        </div>
      )}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => apply("cool")}
          disabled={!!busy}
          data-testid="ac-seasonal-cool"
          className={`flex-1 min-w-[160px] inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-bold text-white transition ${suggested === "cool" ? "bg-sky-600 hover:bg-sky-700 ring-2 ring-sky-300" : "bg-sky-500 hover:bg-sky-600"} disabled:opacity-50`}
        >
          {busy === "cool" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Snowflake className="h-4 w-4" />}
          Estate · Freddo su tutti
        </button>
        <button
          onClick={() => apply("heat")}
          disabled={!!busy}
          data-testid="ac-seasonal-heat"
          className={`flex-1 min-w-[160px] inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-bold text-white transition ${suggested === "heat" ? "bg-orange-700 hover:bg-orange-800 ring-2 ring-orange-300" : "bg-orange-600 hover:bg-orange-700"} disabled:opacity-50`}
        >
          {busy === "heat" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Flame className="h-4 w-4" />}
          Inverno · Caldo su tutti
        </button>
      </div>
    </div>
  );
}
